import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Phone, Check, AlertTriangle, Loader } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import PhoneInput, { isValidPhoneNumber } from './ui/PhoneInput';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MobileOTPLogin = ({ isOpen, onClose, onSuccess }) => {
  const { login } = useAuth();
  
  const [step, setStep] = useState(1); // 1: Enter Phone, 2: Enter OTP
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isNewUser, setIsNewUser] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [devOTP, setDevOTP] = useState(''); // For development mode

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      // Save current scroll position
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    } else {
      // Restore scroll position
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      window.scrollTo(0, parseInt(scrollY || '0') * -1);
    }

    return () => {
      // Cleanup on unmount
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Handle send OTP
  const handleSendOTP = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate E.164 phone
    if (!phone || !isValidPhoneNumber(phone)) {
      setError('Please enter a valid phone number for your country');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/send-otp`, null, {
        params: { phone }
      });

      if (response.data.success) {
        setSuccess(`OTP sent to ${phone}`);
        setStep(2);
        setResendCooldown(30);

        // Dev-mode echo (only when OTP_DEV_ECHO=true on backend). In production
        // WhatsApp is the sole channel — no popup, no visible code on the page.
        if (response.data.dev_mode && response.data.otp_code) {
          setDevOTP(response.data.otp_code);
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle resend OTP
  const handleResendOTP = async () => {
    if (resendCooldown > 0) return;

    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/resend-otp`, null, {
        params: { phone }
      });

      if (response.data.success) {
        setSuccess('New OTP sent successfully');
        setResendCooldown(30);

        if (response.data.dev_mode && response.data.otp_code) {
          setDevOTP(response.data.otp_code);
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  // Handle verify OTP
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError('');

    if (otp.length !== 6) {
      setError('Please enter 6-digit OTP');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/verify-otp`, null, {
        params: {
          phone,
          otp_code: otp,
          full_name: fullName || undefined
        }
      });
      
      if (response.data.success) {
        // Login successful - cookie is set automatically
        login(response.data.user);
        
        setSuccess(response.data.is_new_user ? 'Account created successfully!' : 'Login successful!');
        
        // Close modal after brief delay
        setTimeout(() => {
          if (onSuccess) onSuccess(response.data.user);
          onClose();
          resetForm();
        }, 1000);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || '';
      
      // Check if it's a new user error (asking for name)
      if (errorMsg.includes('Full name is required')) {
        setIsNewUser(true);
        setError('Please enter your full name to create an account');
      } else {
        setError(errorMsg || 'Invalid OTP. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setStep(1);
    setPhone('');
    setOtp('');
    setFullName('');
    setError('');
    setSuccess('');
    setIsNewUser(false);
    setDevOTP('');
  };

  // Close handler
  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[9999]" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}>
      {/* Overlay */}
      <div 
        className="absolute inset-0 bg-black/50"
        onClick={handleClose}
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
      />
      
      {/* Modal Container - Desktop Center, Mobile Bottom */}
      <div 
        className="absolute inset-0 flex items-end md:items-center md:justify-center pointer-events-none"
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
      >
        <div 
          className="bg-white w-full md:w-auto md:max-w-md rounded-t-3xl md:rounded-3xl shadow-2xl pointer-events-auto 
                     max-h-[90vh] overflow-y-auto animate-slide-up md:animate-fade-in"
          onClick={(e) => e.stopPropagation()}
          style={{ 
            position: 'relative',
            marginBottom: '0',
          }}
        >
          {/* Header */}
          <div className="sticky top-0 bg-gradient-to-r from-[#1B375B] to-[#659A9B] px-6 py-4 rounded-t-3xl md:rounded-t-3xl flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <Phone className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-white font-bold text-lg">Login with Mobile</h2>
                <p className="text-white/80 text-xs">Quick & Secure OTP Login</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>

          {/* Body */}
          <div className="p-6 space-y-4">
            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Success Message */}
            {success && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-start gap-2">
                <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-green-700">{success}</p>
              </div>
            )}

            {/* Step 1: Enter Mobile Number */}
            {step === 1 && (
              <form onSubmit={handleSendOTP} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Mobile Number
                  </label>
                  <PhoneInput
                    value={phone}
                    onChange={setPhone}
                    defaultCountry="IN"
                    placeholder="Mobile number"
                    testid="otp-phone"
                    autoFocus
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Select your country and enter your mobile number</p>
                </div>

                <button
                  type="submit"
                  disabled={loading || !isValidPhoneNumber(phone)}
                  className="w-full py-3 bg-[#1B375B] text-white font-bold rounded-xl hover:bg-[#335E77] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      Sending OTP...
                    </>
                  ) : (
                    'Send OTP'
                  )}
                </button>
              </form>
            )}

            {/* Step 2: Enter OTP */}
            {step === 2 && (
              <form onSubmit={handleVerifyOTP} className="space-y-4">
                {/* Show phone number */}
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-semibold">Mobile Number</p>
                    <p className="text-sm font-bold text-gray-800">{phone}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setStep(1);
                      setOtp('');
                      setError('');
                    }}
                    className="text-xs text-[#659A9B] font-semibold hover:underline"
                  >
                    Change
                  </button>
                </div>

                {/* Name input (only for new users) */}
                {isNewUser && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder="Enter your full name"
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#659A9B] focus:border-transparent"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">Required for first-time login</p>
                  </div>
                )}

                {/* OTP Input */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Enter OTP
                  </label>
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#659A9B] focus:border-transparent text-center text-2xl font-bold tracking-widest"
                    maxLength={6}
                    autoFocus
                    required
                  />
                  
                  {/* Delivery channel hint */}
                  <p className="text-xs text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <span className="text-emerald-600">●</span>
                    Sent to your WhatsApp
                  </p>

                  {/* Dev-echo (only shows when OTP_DEV_ECHO=true on backend) */}
                  {devOTP && (
                    <p className="text-xs text-orange-600 mt-1 font-semibold">
                      🔐 Dev Mode: {devOTP}
                    </p>
                  )}

                  {/* Resend OTP */}
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-xs text-gray-500">Didn't receive OTP?</p>
                    {resendCooldown > 0 ? (
                      <p className="text-xs text-gray-500 font-semibold">
                        Resend in {resendCooldown}s
                      </p>
                    ) : (
                      <button
                        type="button"
                        onClick={handleResendOTP}
                        className="text-xs text-[#659A9B] font-semibold hover:underline"
                      >
                        Resend OTP
                      </button>
                    )}
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading || otp.length !== 6 || (isNewUser && !fullName)}
                  className="w-full py-3 bg-[#1B375B] text-white font-bold rounded-xl hover:bg-[#335E77] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      <Check className="w-5 h-5" />
                      Verify & Login
                    </>
                  )}
                </button>
              </form>
            )}

            {/* Continue as Guest */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">or</span>
              </div>
            </div>

            <button
              onClick={handleClose}
              className="w-full py-3 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-colors"
            >
              Continue as Guest
            </button>

            <p className="text-xs text-center text-gray-500">
              Guest bookings will not be saved to your account
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  // Use React Portal to render at document.body level
  return createPortal(modalContent, document.body);
};

export default MobileOTPLogin;
